// Folds the two API reads into the flat requirement list the page renders.
//
// The server sends the record grouped by owner (driver, then each vehicle) and
// the standing breakdown as a per-check list. Neither is what a reviewer scans:
// they want one row per thing that could be wrong, with what to do about it. All
// of that is derived here, in pure functions, so the components stay dumb and
// this file is the only place the mapping can drift.
//
// Nothing here re-derives readiness. `passing`, `isReady` and `isOverallReady`
// come from the server, which computes them with the rules the standing service
// gates on — a second local copy would eventually disagree with the decision.

export const DOC_LABELS = {
  LICENSE: 'Driver license',
  REGISTRATION: 'Registration',
  INSURANCE: 'Insurance',
};

// What a reviewer can DO about a requirement in this state. A status on its own
// isn't actionable; this mapping is what makes a row clickable.
export const REMEDIES = {
  upload: {
    label: 'Upload on their behalf',
    verb: 'Upload',
    note: 'Goes through the same path driver uploads use, so it gets verified',
  },
  review: {
    label: 'Approve or reject',
    verb: 'Decide',
    note: 'Writes the document row and the driver-visible note',
  },
  expiry: {
    label: 'Correct the date, or replace the document',
    verb: 'Fix expiry',
    note: 'EXPIRED is derived from the date — fix the date, never the status',
  },
  reverse: {
    label: 'Reverse the rejection, or take a new upload',
    verb: 'Reverse',
    note: 'Admin decisions are not blocked by the current status',
  },
  adjudicate: {
    label: 'Resolve the background check',
    verb: 'Adjudicate',
    note: 'A standing action — never a document write',
  },
};

// Status of the CURRENT row → the remedy for it. APPROVED is absent: there is
// nothing to resolve, though the row still opens read-only.
const REMEDY_BY_STATUS = {
  MISSING: 'upload',
  PENDING: 'review',
  NEEDS_REVIEW: 'review',
  REJECTED: 'reverse',
  EXPIRED: 'expiry',
};

/**
 * Whose turn it is. The remedy says what to do about a row; this says who has to
 * do it — which is the question a reviewer working a queue is actually asking, and
 * the one every non-approved status having a remedy was hiding.
 *
 * Keyed on status rather than remedy, because PENDING and NEEDS_REVIEW share the
 * `review` remedy and are NOT the same work. NEEDS_REVIEW is the pipeline escalating:
 * it looked, could not settle it, and a person is the named next step. PENDING means
 * nothing has looked yet — so it is `system`, not ours. Only NEEDS_REVIEW is "needs
 * review".
 *
 * The driver's are the ones a new upload resolves. An admin uploading or correcting
 * on their behalf is a courtesy, not work anybody is waiting on us for.
 */
const WAITING_ON = {
  NEEDS_REVIEW: 'us',
  PENDING: 'system',
  MISSING: 'driver',
  EXPIRED: 'driver',
  REJECTED: 'driver',
};

/**
 * Post-approval states, in standing's own vocabulary. Keys match the API's values.
 *
 * These are STATES, not verbs, so the menu and the pill say the same word — a menu of
 * "Approve / Send to review / Suspend" next to a pill reading "Cleared" gives a
 * reviewer nothing to match their current status against.
 *
 * `action` is the verb the endpoint actually takes, because PATCH /standing is keyed
 * on actions, not on target states — sending a state 400s with a list of the eight
 * actions. A state with no `action` is one the server has no verb for, and is listed
 * only so the pill's vocabulary is complete in the menu.
 *
 * The two derived ones roll up from document validity and are unsettable for that
 * reason: a reviewer who could pick Cleared could mark a driver cleared over an
 * expired document, which is the desync this rebuild exists to fix. Cleared is
 * settable only as a way BACK — from suspended or deactivated it maps to `reinstate`,
 * a human decision the server transcribes rather than derives.
 */
export const STANDING_STATES = [
  {
    key: 'cleared',
    value: 'CLEARED',
    action: 'reinstate',
    hint: 'Good standing — normal driver home',
    unsettableHint: 'Set by documents — resolve the requirements below',
    palette: 'success',
  },
  { key: 'invalid_information', value: 'INVALID_INFORMATION', unsettableHint: 'Set by documents' },
  {
    key: 'under_review',
    value: 'UNDER_REVIEW',
    action: 'send_to_review',
    hint: 'Pre-adjudication hold',
    palette: 'warning',
  },
  { key: 'suspended', value: 'SUSPENDED', action: 'suspend', hint: 'Temporary, reversible', palette: 'error' },
  {
    key: 'deactivated',
    value: 'DEACTIVATED',
    action: 'deactivate',
    hint: 'Terminal — needs a reason',
    palette: 'error',
    needsReason: true,
  },
];

export const STANDING_LABELS = {
  CLEARED: 'Cleared',
  INVALID_INFORMATION: 'Invalid information',
  SUSPENDED: 'Suspended',
  UNDER_REVIEW: 'Under review',
  DEACTIVATED: 'Deactivated',
};

/**
 * Pre-approval states: the five the driver's own app shows them, not the fourteen
 * stored funnel values.
 *
 * This is the RN app's vocabulary (`driverApplicationHomeState`), collapsed server-side
 * by `toExternalDriverStatus`. Admin and the driver therefore describe the application
 * the same way, which is the point — a reviewer telling someone "you're under review"
 * should be reading the same words that person sees.
 *
 * The stored funnel is deliberately NOT exposed here. Several of its values are steps
 * the pipeline walks rather than states a human picks (`background_check`,
 * `selfie_manual_review`), and several are legacy values RN never had. /drivers-approval
 * remains the surface for the raw column.
 *
 * Only three of the five are settable, because APPLICATION_ACTIONS has a verb for only
 * three: `request_documents`, `approve`, `reject`. There is no action that writes
 * `in_progress` — that is the driver's own progress through the form — and none that
 * writes a pre-approval `in_review`: the endpoint's `send_to_review` is a STANDING
 * action and the server refuses it for an applicant, because it maps to `consider`,
 * which only means something after approval. Both are listed anyway, disabled, so a
 * reviewer can find their driver's current status in the menu.
 *
 * `waitlist` is the fourth application action and has no state here on purpose: the
 * collapse folds it into `in_review`, so exposing "Under review" as settable would
 * have quietly waitlisted people.
 */
export const APPLICATION_STATES = [
  {
    key: 'in_progress',
    value: 'in_progress',
    hint: 'Part-way through, not submitted',
    unsettableHint: 'Set by their own progress through the form',
  },
  {
    key: 'in_review',
    value: 'in_review',
    hint: 'Waiting on us — no driver action',
    unsettableHint: 'Set when they submit, and by the pipeline after that',
    palette: 'accent',
  },
  {
    key: 'update_application',
    value: 'update_application',
    action: 'request_documents',
    hint: 'Waiting on them — sends them to the document hub',
    palette: 'warning',
  },
  {
    key: 'approved',
    value: 'approved',
    action: 'approve',
    hint: 'Standing takes over from here',
    palette: 'success',
    // Approving latches hasBeenApproved permanently. See confirmCopy.
    confirm: true,
  },
  { key: 'rejected', value: 'rejected', action: 'reject', hint: 'Ends the application', palette: 'error' },
];

/**
 * The five, labelled. Shorter than the driver's own headings but the same words:
 * RN shows "Application under review", "Update your application", "Application not
 * approved".
 *
 * One map for both the pill and the list filter, because under this vocabulary they are
 * the same five values — the filter is no longer a coarser grouping over something else.
 */
export const APPLICATION_STATUS_LABELS = {
  in_progress: 'In progress',
  in_review: 'Under review',
  update_application: 'Update requested',
  approved: 'Approved',
  rejected: 'Not approved',
};

const ADMIN_APPLICATION_STATUS = {
  in_progress: 'in_progress',
  in_review: 'submitted',
  update_application: 'awaiting_docs',
  approved: 'approved',
  rejected: 'rejected',
};

export const adminApplicationStatusFor = (value) => ADMIN_APPLICATION_STATUS[value];

const APPLICATION_STATUS_PALETTE = {
  in_progress: null,
  in_review: 'accent',
  update_application: 'warning',
  approved: 'success',
  rejected: 'error',
};

const STANDING_PALETTE = {
  CLEARED: 'success',
  INVALID_INFORMATION: 'warning',
  UNDER_REVIEW: 'warning',
  SUSPENDED: 'error',
  DEACTIVATED: 'error',
};

/**
 * The label for one state, in whichever vocabulary its phase uses.
 *
 * A pre-approval value the collapse did not recognise arrives as null — which is a real
 * answer ("we do not know what state this is"), not a blank.
 */
export const stateLabel = ({ hasBeenApproved, value }) => {
  if (!value) return hasBeenApproved ? '—' : 'Unknown status';
  return hasBeenApproved ? STANDING_LABELS[value] || value : APPLICATION_STATUS_LABELS[value] || value;
};

/**
 * The headline: which phase of their life this driver is in, then where in it.
 *
 * Phase first because the same word means different things on either side of the
 * latch — `awaiting_docs` pre-approval is someone still applying, and post-approval is
 * a working driver whose documents went bad, because standing mirrors down onto the
 * funnel. Without the phase the pill is ambiguous exactly where being wrong costs the
 * most.
 *
 * Pre-approval this reads the collapsed status — the same five the driver's own app
 * shows — so the pill names a value the menu below it can be set to.
 */
export const phaseStatus = ({ hasBeenApproved, currentStanding, applicationStatus }) => {
  const value = hasBeenApproved ? currentStanding : applicationStatus;
  return {
    phase: hasBeenApproved ? 'Driver' : 'Applicant',
    label: stateLabel({ hasBeenApproved, value }),
    palette: hasBeenApproved ? STANDING_PALETTE[currentStanding] : APPLICATION_STATUS_PALETTE[applicationStatus],
  };
};

export const vehicleLabel = (v) => [v.year, v.make, v.model].filter(Boolean).join(' ');

/**
 * A stored moment, from whichever shape the source hands us.
 *
 * The bug this exists for: the funnel history arrives over GraphQL, which serialises
 * the Mongo BSON date as epoch milliseconds — and as a STRING, which `new Date()`
 * reads as an invalid date rather than as a number. So every row rendered with a blank
 * time AND in stored order, because the same failed parse made the formatter return
 * null and made every sort comparison NaN. The REST endpoints on this page send ISO
 * strings, and the users page reads the very same Mongo field over REST, which is why
 * its own history card never hit this.
 *
 * Null for anything unparseable, which the caller renders as a dash rather than
 * dropping — see statusHistory.
 */
const toDate = (value) => {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  // All-digits means epoch millis, and those must go through Number() first.
  const parsed =
    typeof value === 'number'
      ? new Date(value)
      : typeof value === 'string'
      ? new Date(/^\d+$/.test(value.trim()) ? Number(value.trim()) : value)
      : null;
  return parsed && !Number.isNaN(parsed.getTime()) ? parsed : null;
};

/** `needs_review` → `Needs review`. Stored values, made readable, not translated. */
const humanize = (value) => value.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

/**
 * One merged timeline, newest first, from every source this page already holds.
 *
 * Merged rather than filtered by a control, because the value of a log is the
 * adjacency BETWEEN sources — "license rejected, then standing went invalid, then the
 * funnel mirrored to consider" is one story in three rows, and a switch that shows one
 * kind at a time hides the only thing the list is for.
 *
 * All three sources are here. Application status comes from the Mongo history the
 * funnel query returns; document events are derived from the record's own rows;
 * standing changes come from `driver_standing_event` on the standing breakdown.
 *
 * Standing rows are the ones carrying `reason` and `source` — who suspended this
 * driver and why. Nothing else on the page can answer that: standing mirrors DOWN
 * onto the funnel, so the same suspension appears in the application trail as a bare
 * `Consider` with its reason stripped off. Both rows are kept rather than deduped;
 * they are two writes, and seeing the mirror land is how you tell a standing change
 * from someone editing the funnel directly.
 *
 * Application rows carry the RAW stored value, not the collapsed five. That is the
 * point: the pill answers "what state are they in" in the driver's words, and this
 * answers "how did they get here" in the system's, where `background_check` and
 * `consider` are distinct facts the pill flattens into one word.
 *
 * Sorted here rather than trusted — the Mongo array has no ordering guarantee, and a
 * trail that is subtly out of order is worse than one that is obviously unsorted.
 * Duplicates are kept: a status re-written is an event. An entry whose timestamp will
 * not parse is kept too, with a null time, and sorts last; dropping events because a
 * date is in an unexpected shape would quietly shorten an audit trail, which is the
 * one thing an audit trail must never do.
 */
export const eventLog = ({ driver, profile, standing }) => {
  const application = (driver?.driverApplicationStatusUpdates || [])
    .filter((entry) => entry?.status)
    .map((entry, i) => ({
      // Index in the key because the same status at the same instant is possible
      // (two writers, one round trip) and React needs them distinguishable.
      key: `status-${entry.status}-${entry.timestamp}-${i}`,
      kind: 'Application',
      label: humanize(entry.status),
      detail: null,
      date: toDate(entry.timestamp),
    }));

  // Which owner a requirement hangs off, so two vehicles' insurance uploads are
  // distinguishable — both rows would otherwise read "Insurance uploaded".
  const ownerTitle = new Map((profile?.owners || []).map((owner) => [owner.key, owner.title]));

  const documents = (profile?.requirements || []).flatMap((requirement) =>
    (requirement.rows || []).map((row) => ({
      key: `doc-${row.id}`,
      kind: 'Document',
      // Dated by `createdAt`, which is the UPLOAD. A decision has no timestamp of its
      // own on the row, so the status is carried as context rather than as its own
      // event — claiming the upload time as the decision time would be a wrong fact.
      label: `${requirement.label} uploaded`,
      detail: [
        requirement.level === 'vehicle' ? ownerTitle.get(requirement.group) : null,
        row.source ? `by ${row.source}` : null,
        `now ${humanize(row.status).toLowerCase()}`,
        row.reviewNote ? `“${row.reviewNote}”` : null,
      ]
        .filter(Boolean)
        .join(' · '),
      date: toDate(row.createdAt),
    })),
  );

  // `source` is an admin email for a human decision and a job or webhook name for a
  // derived one — "who did this" in both cases, which is why it is not dropped when
  // it does not look like a person. Null on events written before the column existed.
  const standings = (standing?.history || []).map((event, i) => ({
    key: `standing-${event.at}-${i}`,
    kind: 'Standing',
    // The state alone. The kind column already says "Standing", so `Standing → Cleared`
    // said it twice — and the Application rows beside it have always been the bare
    // status, so this was also the only row shape in the log with an arrow in it.
    label: STANDING_LABELS[event.standing] || event.standing,
    detail: [event.reason, event.source ? `by ${event.source}` : null].filter(Boolean).join(' · ') || null,
    date: toDate(event.at),
  }));

  return [...application, ...documents, ...standings]
    .sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0))
    .map(({ date, ...entry }) => ({ ...entry, at: date ? timestamp(date) : null }));
};

/**
 * A moment, in the reader's own timezone, named.
 *
 * Everything is stored and sent as UTC, and a reviewer thinks in local time — so
 * these render local (which `toLocaleString` does by default) and print the zone
 * abbreviation, because an unlabelled time next to a UTC-looking API is ambiguous
 * exactly when someone is trying to reconcile the two.
 *
 * Seconds included: webhooks arrive in bursts and uploads get retried, so entries a
 * few seconds apart are common and minute precision makes them look simultaneous.
 */
export const timestamp = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

/**
 * A calendar date, for a value that carries no time of day.
 *
 * Formatted in UTC on purpose, and this is not a detail. A document's expiry is a
 * `date` column and arrives as a bare `YYYY-MM-DD`, which `new Date` parses as UTC
 * midnight — so formatting it in local time renders the PREVIOUS DAY for every
 * reader west of UTC. An expiry is a day, not a moment; shifting it by a timezone
 * changes what it says.
 *
 * No zone name for the same reason: there is no zone to name.
 */
export const calendarDate = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const shortDate = calendarDate;

const hoursSince = (value) => {
  if (!value) return null;
  return Math.floor((Date.now() - new Date(value).getTime()) / 3_600_000);
};

const ago = (value) => {
  const hours = hoursSince(value);
  if (hours === null) return null;
  if (hours < 1) return 'less than an hour ago';
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'yesterday' : `${days}d ago`;
};

/**
 * Index the standing breakdown so a document group can find its own check.
 *
 * Keyed on the triple that identifies a requirement — type, level, and which
 * vehicle for a vehicle-level one. Type alone collides: two vehicles both need
 * insurance and they pass independently.
 */
const checkKey = (type, level, driverVehicleId) => `${level}:${type}:${driverVehicleId || ''}`;

const indexChecks = (standing) => {
  const map = new Map();
  // The breakdown nests documents under their owner, so `level` and `driverVehicleId`
  // come from POSITION rather than from fields on each document — they used to be
  // repeated on every entry of one flat array purely so this function could regroup
  // them. The key it builds is unchanged, so nothing downstream moves.
  (standing?.driver?.documents || []).forEach((check) => {
    map.set(checkKey(check.documentType, 'driver', null), check);
  });
  (standing?.vehicles || []).forEach((vehicle) => {
    (vehicle.documents || []).forEach((check) => {
      map.set(checkKey(check.documentType, 'vehicle', vehicle.driverVehicleId), check);
    });
  });
  return map;
};

/**
 * Does the stored `driver_vehicle.status` contradict what the documents say?
 *
 * A straight comparison against the API's `derivedStatus`, which is what the backend
 * reconciles the column to on every standing evaluation. This used to re-derive the
 * answer here from `isReady` — a second, weaker copy of a rule that now has one owner,
 * and one that could only catch the two flagrant directions (APPROVED over an unready
 * vehicle, REJECTED over a ready one). A PENDING left on a fully approved vehicle, which
 * is the case that actually reached us, slipped straight through it.
 *
 * `derived` being absent means the API predates that field: report no drift rather than
 * flagging every vehicle on an older backend.
 */
export const driftedStatus = (stored, derived) => {
  if (!stored || !derived) return false;
  return stored !== derived;
};

/**
 * One requirement row from a document group.
 *
 * `status` is the current row's stored status — what to show. `passing` comes
 * from the standing breakdown and is what actually gates: a pending re-upload
 * sitting over a still-valid approved document is *actionable but not blocking*,
 * and collapsing those two into one flag is how a reviewer ends up chasing a
 * requirement that was never in the way.
 */
const requirementFromGroup = (group, { level, driverVehicleId, checks }) => {
  const check = checks.get(checkKey(group.type, level, driverVehicleId));
  const current = group.rows.find((r) => r.isCurrent) || null;
  const remedy = group.required ? REMEDY_BY_STATUS[group.status] || null : null;
  // No check row means the standing service doesn't gate on this type for this
  // driver's state. Treat it as passing so an un-required extra document never
  // reads as blocking.
  const passing = check ? check.passing : true;

  return {
    // Stable across re-reads: the row id moves when a new version is uploaded,
    // the requirement it satisfies does not. Selection survives a refresh.
    id: checkKey(group.type, level, driverVehicleId),
    group: level === 'driver' ? 'driver' : driverVehicleId,
    level,
    driverVehicleId: driverVehicleId || null,
    documentType: group.type,
    label: DOC_LABELS[group.type] || group.type,
    status: group.status,
    required: group.required,
    passing,
    blocking: group.required && !passing,
    remedy,
    waitingOn: remedy ? WAITING_ON[group.status] || null : null,
    documentId: current ? current.id : null,
    current,
    rows: group.rows,
    detail: groupDetail(group, current),
    why: remedy ? groupWhy(group, current, passing) : null,
  };
};

/** The one-line summary under a row's title. */
const groupDetail = (group, current) => {
  if (group.status === 'MISSING') {
    return group.required ? 'Nothing on file' : 'Not required in this state';
  }
  const bits = [];
  if (current?.expirationDate) {
    const expires = shortDate(current.expirationDate);
    bits.push(group.status === 'EXPIRED' ? `Expired ${expires}` : `Expires ${expires}`);
  }
  if (group.rows.length > 1) bits.push(`v${group.rows.length}`);
  if (current?.source) bits.push(`uploaded by ${current.source}`);
  return bits.join(' · ') || 'On file';
};

/** Why this row needs attention — the sentence above its remedy. */
const groupWhy = (group, current, passing) => {
  const when = current ? ago(current.createdAt) : null;
  switch (group.status) {
    case 'MISSING':
      return 'No document of this type has ever been uploaded.';
    case 'EXPIRED':
      return `Expired ${shortDate(current?.expirationDate) || 'and has no expiry on file'}. Correcting a misread date is a decision, not a re-upload.`;
    case 'REJECTED':
      return current?.reviewNote
        ? `Rejected: “${current.reviewNote}”`
        : 'Rejected with no note on file, so the driver was told no without being told why.';
    case 'NEEDS_REVIEW':
      return `Automated verification could not settle this one${when ? `, uploaded ${when}` : ''}.`;
    case 'PENDING':
      // "Waiting on review" is what this used to say, and it was wrong in the way
      // that matters: PENDING means nothing has looked at it, and it does not clear
      // itself unless the document pipeline is applying decisions for this driver.
      // Only NEEDS_REVIEW is a person's queue.
      return passing
        ? `A newer upload${when ? ` from ${when}` : ''} has not been verified yet, but the previous approved document is still valid — this is not blocking them.`
        : `Not verified yet${when ? `, uploaded ${when}` : ''} — nothing has looked at it.`;
    default:
      return null;
  }
};

/** The background check as one more requirement row, so it scans with the rest. */
const requirementFromScreening = (backgroundCheck) => {
  const { state, result, completedAt, passing } = backgroundCheck || {};
  const status = {
    absent: 'MISSING',
    pending: 'PENDING',
    cancelled: 'REJECTED',
    completed: passing ? 'APPROVED' : 'NEEDS_REVIEW',
  }[state] || 'MISSING';

  const detail = {
    // Absent is normal, not a gap: a check is triggered when a driver accepts a
    // trip, so one who has not accepted any has nothing to show.
    absent: 'Not run — triggered when they accept their first trip',
    pending: 'Running at Checkr',
    cancelled: 'Cancelled — it will never produce a result',
    completed: `Checkr — ${result || 'no result'}${completedAt ? `, completed ${shortDate(completedAt)}` : ''}`,
  }[state] || 'Unknown';

  // Absent and pending don't block, and there is nothing for a reviewer to do
  // about either, so neither gets a remedy.
  const remedy = (state === 'completed' && !passing) || state === 'cancelled' ? 'adjudicate' : null;

  return {
    id: 'screening',
    group: 'driver',
    level: 'driver',
    driverVehicleId: null,
    documentType: null,
    label: 'Background check',
    status,
    required: true,
    passing: Boolean(passing),
    blocking: !passing && (state === 'completed' || state === 'cancelled'),
    remedy,
    // Not from WAITING_ON: this row's status is synthesised from the screening state,
    // so a completed-but-failing check reads NEEDS_REVIEW there but the work is a
    // standing adjudication. Either way it is a person's, which is what matters here.
    waitingOn: remedy ? 'us' : null,
    documentId: null,
    current: null,
    rows: [],
    detail,
    why:
      state === 'cancelled'
        ? 'The report was cancelled, so no result is coming. Re-run it or decide without one.'
        : state === 'completed' && !passing
        ? `Checkr returned “${result}”. This is a standing decision — approving it here does not change the report.`
        : null,
  };
};

/**
 * Everything the page renders, from the two reads.
 *
 * Owners come out in the order a reviewer works: the driver first (a bad license
 * blocks every vehicle, so it is never worth opening a vehicle before it), then
 * the default vehicle, then the rest.
 */
export const buildProfile = ({ record, standing, driver }) => {
  const checks = indexChecks(standing);
  // `driver_vehicle.status` as stored, and what the same response derived it should be,
  // both keyed by vehicle. Surfaced on the card so an admin can see the column — the
  // backend reconciles it to the documents on every evaluation, so the two agreeing is now
  // the norm and a difference is a row that write has not reached. Nothing here derives
  // from either: readiness is still what the rest of the page reads.
  const storedStatus = new Map(
    (standing?.vehicles || []).map((v) => [v.driverVehicleId, v.status ?? null]),
  );
  const derivedStatus = new Map(
    (standing?.vehicles || []).map((v) => [v.driverVehicleId, v.derivedStatus ?? null]),
  );

  const driverRequirements = [
    ...(record?.driverDocuments || []).map((group) =>
      requirementFromGroup(group, { level: 'driver', driverVehicleId: null, checks }),
    ),
    requirementFromScreening(standing?.backgroundCheck),
  ];

  const vehicles = [...(record?.vehicles || [])]
    .sort((a, b) => Number(b.isDefault) - Number(a.isDefault))
    .map((vehicle) => ({
      ...vehicle,
      label: vehicleLabel(vehicle),
      recordStatus: storedStatus.get(vehicle.driverVehicleId) ?? null,
      // The stored column disagreeing with what this same response derived. The backend
      // reconciles the two on every evaluation, so a difference means the write has not
      // reached this row yet — worth showing, and no longer the norm it used to be.
      statusDrifted: driftedStatus(
        storedStatus.get(vehicle.driverVehicleId) ?? null,
        derivedStatus.get(vehicle.driverVehicleId) ?? null,
      ),
      requirements: (vehicle.documents || []).map((group) =>
        requirementFromGroup(group, {
          level: 'vehicle',
          driverVehicleId: vehicle.driverVehicleId,
          checks,
        }),
      ),
    }));

  const owners = [
    { key: 'driver', title: 'Driver', kind: 'driver', requirements: driverRequirements },
    ...vehicles.map((v) => ({ key: v.driverVehicleId, title: v.label, kind: 'vehicle', vehicle: v, requirements: v.requirements })),
  ];

  const requirements = owners.flatMap((o) => o.requirements);

  return {
    owners,
    vehicles,
    requirements,
    actions: requirements.filter((r) => r.remedy),
    // The subset of actions that are OURS. Separate from `actions` because they are
    // the only rows a reviewer can clear — the rest sit there until the driver
    // re-uploads, and mixing the two is what made the to-do list unreadable.
    reviewQueue: requirements.filter((r) => r.waitingOn === 'us'),
    // Blocking is the count that matters for "why isn't this driver cleared" —
    // it excludes the actionable-but-harmless pending-over-valid case.
    blockingCount: requirements.filter((r) => r.blocking).length,
    isOverallReady: Boolean(record?.isOverallReady),
    // What their standing IS. Never substitute derivedState for it: a suspended
    // driver whose documents are all valid derives CLEARED.
    currentStanding: standing?.currentStanding || null,
    derivedState: standing?.derivedState || null,
    // A decision the current facts no longer support — worth surfacing, because
    // it is exactly the desync this page exists to make visible.
    standingDiverged: Boolean(
      standing?.currentStanding && standing?.derivedState && standing.currentStanding !== standing.derivedState,
    ),
    documentsPassing: Boolean(standing?.documentsPassing),
    backgroundCheck: standing?.backgroundCheck || null,
    // Both come from the server rather than being re-derived here: the latch is read
    // off the immutable status history, and the status is collapsed by the same
    // mapping the driver's own app uses — so old-app values resolve too.
    hasBeenApproved: Boolean(standing?.hasBeenApproved),
    // The collapsed status, from the server. The five the driver's own app shows, which
    // are also the five the status menu sets — so the pill always names something the
    // menu contains.
    applicationStatus: standing?.applicationStatus || null,
  };
};

/**
 * Which actions to offer, given which side of the approval latch they are on.
 *
 * The split is the server's rule, not a presentation choice — sending a funnel move
 * for an approved driver, or a standing action for an applicant, is a 400. Offering
 * both and disabling half would be a longer menu that says the same thing.
 *
 * Within standing: deactivated is terminal, so the only way out is reinstatement;
 * offering suspend on an already-deactivated driver would write a state the funnel
 * treats as recoverable. Reinstate is hidden when they are already cleared because
 * it would be a no-op write with an audit event attached.
 */
export const availableActions = (
  { hasBeenApproved, currentStanding, applicationStatus },
  { allowAll = false } = {},
) => {
  const current = hasBeenApproved ? currentStanding : applicationStatus;
  const states = hasBeenApproved ? STANDING_STATES : APPLICATION_STATES;

  return states.map((state) => {
    const isCurrent = state.value === current;
    // Every state the pill can show stays in the list; what changes is whether it can
    // be chosen. Removing them instead would leave a reviewer looking for their own
    // driver's status in a menu that does not contain it.
    const disabled =
      isCurrent ||
      (!allowAll &&
        // No verb, no write. The endpoint takes actions, so a state the server has no
        // action for cannot be set from anywhere — offering it produced a 400 listing
        // the eight valid actions, which is what this now prevents.
        (!state.action ||
          // Cleared over failing documents is the one assertion a human must not make —
          // it would be true until the next re-derive silently flipped it back. Choosing
          // it FROM suspended or deactivated is the way back, and is allowed.
          (state.key === 'cleared' && currentStanding === 'INVALID_INFORMATION') ||
          // Terminal: the only move off deactivated is back to cleared.
          (hasBeenApproved && currentStanding === 'DEACTIVATED' && state.key !== 'cleared')));

    return {
      ...state,
      label: stateLabel({ hasBeenApproved, value: state.value }),
      isCurrent,
      disabled,
      disabledHint: isCurrent ? 'Current status' : state.unsettableHint,
    };
  });
};

/**
 * The states re-evaluation owns. Anything else on a driver is a DECISION — an admin's
 * call, or the score path's suspension — and the server leaves those alone.
 */
const DERIVABLE_STANDINGS = ['CLEARED', 'UNDER_REVIEW', 'INVALID_INFORMATION'];

/**
 * Whether re-evaluating this driver's standing would do anything.
 *
 * A COPY of two server rules, both in hitch-services
 * `apps/admin/src/v1/driver-standing.controller.ts` and the `DriverStandingService`
 * behind it: the endpoint 400s for a driver who has never been approved, and the
 * service declines to write over a standing it did not derive. Duplicated so the menu
 * can NAME the reason in place, rather than a reviewer discovering it by clicking and
 * reading an error toast — and, for the suspended case, by clicking and seeing nothing
 * happen at all, since that one is a silent no-op rather than an error.
 *
 * The server stays the enforcer. This is presentation; `useDriverRecord` still shows
 * whatever the server says if the two ever disagree.
 *
 * Not relaxed by the pill's `allowAll`. That flag widens which states an admin may SET;
 * these two cases are server refusals, and a client flag cannot lift them.
 */
export const canReevaluateStanding = ({ hasBeenApproved, currentStanding }) => {
  if (!hasBeenApproved) {
    return { enabled: false, hint: 'The funnel still owns applicants — standing starts at approval' };
  }
  if (!DERIVABLE_STANDINGS.includes(currentStanding)) {
    return { enabled: false, hint: 'A decision, not a derived state — re-evaluation leaves it alone' };
  }
  return { enabled: true, hint: 'Recompute from documents and background check' };
};
